<template>
  <div class="fixed inset-0 z-40 bg-black/30 flex items-center justify-center" @click.self="$emit('close')">
    <div class="bg-white rounded-lg border border-gray-200 w-[52rem] max-w-[95vw] h-[80vh] flex flex-col">
      <div class="px-4 py-3 border-b border-gray-100 flex items-center gap-4 shrink-0">
        <h3 class="text-sm font-semibold text-gray-900">Scope</h3>
        <div class="flex items-center gap-1">
          <button type="button" class="sc-tab" :class="{ 'is-on': view === 'tables' }" @click="view = 'tables'">Tables</button>
          <button type="button" class="sc-tab" :class="{ 'is-on': view === 'tools' }" @click="view = 'tools'">Tools</button>
          <button type="button" class="sc-tab" :class="{ 'is-on': view === 'hats' }" @click="view = 'hats'">Hats</button>
        </div>
        <input
          v-model.trim="search"
          type="text"
          :placeholder="view === 'tables' ? 'Search tables…' : view === 'hats' ? 'Search hats…' : 'Search tools…'"
          class="ml-auto border border-gray-200 rounded px-2 py-1 text-xs w-56 focus:outline-none focus:border-gray-400"
        />
        <button class="text-gray-400 hover:text-gray-600" @click="$emit('close')">×</button>
      </div>

      <div v-if="view === 'tables'" class="sc-body">
        <div class="sc-summary">Warehouse tables the agent may query<span v-if="tableCount" class="sc-count">{{ tableCount }}</span></div>
        <p v-if="loading" class="sc-note">Loading catalog…</p>
        <p v-else-if="!schemas.length" class="sc-note">Catalog unavailable.</p>
        <table v-else class="sc-table">
          <colgroup><col class="sc-col-name"><col></colgroup>
          <thead><tr><th>Table</th><th>What it holds</th></tr></thead>
          <tbody v-for="schema in filteredSchemas" :key="schema.name">
            <tr class="sc-group"><td colspan="2"><span class="sc-group-name">{{ schema.name }}</span><span class="sc-count">{{ schema.tables.length }}</span></td></tr>
            <tr v-for="t in schema.tables" :key="t.name">
              <td class="sc-name">{{ t.name }}</td>
              <td class="sc-desc">{{ t.description }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-else-if="view === 'hats'" class="sc-body">
        <div class="sc-summary">The roles the agent works in, and which one it is wearing now<span v-if="hats.length" class="sc-count">{{ hats.length }}</span></div>
        <p v-if="!tools" class="sc-note">Start a session to see its hats.</p>
        <p v-else-if="!hats.length" class="sc-note">This agent has no hats defined.</p>
        <table v-else class="sc-table">
          <colgroup><col class="sc-col-name"><col></colgroup>
          <thead><tr><th>Hat</th><th>What it is for</th></tr></thead>
          <tbody>
            <tr v-for="h in hats" :key="h.name" :class="{ 'sc-hat-on': isOn(h) }">
              <td class="sc-name"><span v-if="isOn(h)" class="sc-ok">●</span>{{ h.label }}<span class="sc-family">{{ h.family }}</span></td>
              <td class="sc-desc">{{ h.description }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-else class="sc-body">
        <p v-if="!tools" class="sc-note">Start a session to see the tools its agent was given.</p>
        <template v-else>
          <div class="sc-summary">What the agent can do<span class="sc-count">{{ tools.available.length }}</span></div>
          <table class="sc-table">
            <colgroup><col class="sc-col-name"><col></colgroup>
            <thead><tr><th>Tool</th><th>What it does</th></tr></thead>
            <tbody v-for="group in groupedTools" :key="group.name">
              <tr class="sc-group"><td colspan="2"><span class="sc-group-name">{{ group.label }}</span><span class="sc-count">{{ group.items.length }}</span></td></tr>
              <tr v-for="t in group.items" :key="t.name">
                <td class="sc-name"><span class="sc-ok">✓</span>{{ t.name }}</td>
                <td class="sc-desc">{{ t.description }}</td>
              </tr>
            </tbody>
          </table>
          <div class="sc-summary sc-summary-gap">What it cannot do<span class="sc-count">{{ tools.unavailable.length }}</span></div>
          <table class="sc-table">
            <colgroup><col class="sc-col-name"><col></colgroup>
            <thead><tr><th>Capability</th><th>Why not</th></tr></thead>
            <tbody>
              <tr v-for="t in tools.unavailable" :key="t.name" class="sc-no">
                <td class="sc-name"><span class="sc-x">✕</span>{{ t.name }}</td>
                <td class="sc-desc">{{ t.why }}</td>
              </tr>
            </tbody>
          </table>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

const GROUP_LABELS = {
  shared: 'Session', reference: 'Reference', metric: 'Metrics', ingestion: 'Ingestion', analysis: 'Studies and datasets', intelligence: 'Kairon Intelligence', patient: 'Patient graph', documents: 'Documents', reports: 'Reports', workflow: 'Workflow configuration', workflow_builder: 'Workflow Builder', analyses: 'Analyses', library: 'Library', support: 'Service Desk', pages: 'Pages', drafts: 'Drafts',
};
const GROUP_ORDER = ['shared', 'reference', 'library', 'support', 'drafts', 'documents', 'reports', 'metric', 'workflow_builder', 'workflow', 'analyses', 'analysis', 'intelligence', 'patient', 'pages', 'ingestion'];

export default {
  name: 'AgentScopeModal',
  props: {
    tools: { type: Object, default: null },
    hat: { type: Object, default: null },
    initialView: { type: String, default: 'tables' },
  },
  emits: ['close'],
  data() {
    return { view: this.initialView, schemas: [], loading: true, search: '' };
  },
  computed: {
    tableCount() {
      return this.schemas.reduce((n, s) => n + s.tables.length, 0);
    },
    filteredSchemas() {
      const q = this.search.toLowerCase();
      if (!q) return this.schemas;
      return this.schemas
        .map((s) => ({
          name: s.name,
          tables: s.tables.filter((t) => t.name.toLowerCase().includes(q)
            || (t.description || '').toLowerCase().includes(q)),
        }))
        .filter((s) => s.tables.length);
    },
    hats() {
      const q = this.search.toLowerCase();
      return ((this.tools && this.tools.hats) || []).filter((h) => !q
        || h.label.toLowerCase().includes(q) || (h.description || '').toLowerCase().includes(q));
    },
    groupedTools() {
      if (!this.tools) return [];
      const q = this.search.toLowerCase();
      const items = (this.tools.available || []).filter((t) => !q
        || t.name.toLowerCase().includes(q) || (t.description || '').toLowerCase().includes(q));
      const groups = {};
      items.forEach((t) => {
        const g = t.group || 'shared';
        (groups[g] = groups[g] || []).push(t);
      });
      return Object.keys(groups)
        .sort((a, b) => GROUP_ORDER.indexOf(a) - GROUP_ORDER.indexOf(b))
        .map((name) => ({ name, label: GROUP_LABELS[name] || name, items: groups[name] }));
    },
  },
  async mounted() {
    try {
      const { data } = await axios.get('/api/impact_analytics/warehouse-catalog/');
      this.schemas = (data.schemas || [])
        .map((s) => ({
          name: s.name,
          tables: Object.entries(s.tables || {})
            .filter(([, t]) => t.use_for_metrics)
            .map(([name, t]) => ({ name, description: t.description || '' })),
        }))
        .filter((s) => s.tables.length);
    } catch {
      this.schemas = [];
    }
    this.loading = false;
  },
  methods: {
    isOn(h) {
      return !!(this.hat && (this.hat.names || []).includes(h.name));
    },
  },
};
</script>

<style scoped>
.sc-tab { font-size: 12px; padding: 4px 10px; border-radius: 4px; border: 0; background: transparent; color: #56534d; cursor: pointer; font-family: inherit; }
.sc-tab.is-on { background: #faf9f6; color: #1a1918; font-weight: 600; }
.sc-body { padding: 14px 16px; overflow-y: auto; flex: 1; font-family: 'IBM Plex Sans', Helvetica, Arial, sans-serif; color: #38352f; }
.sc-summary { display: flex; align-items: center; gap: 8px; font-size: 12.5px; font-weight: 600; color: #1a1918; margin-bottom: 8px; }
.sc-summary-gap { margin-top: 22px; }
.sc-count { display: inline-block; font-size: 10.5px; font-weight: 600; color: #56534d; background: #f0eee8; border-radius: 999px; padding: 1px 7px; }
.sc-note { font-size: 12px; color: #56534d; }
.sc-table { width: 100%; border-collapse: collapse; border: 1px solid #e4e0d9; border-radius: 6px; overflow: hidden; font-size: 12px; }
.sc-col-name { width: 34%; }
.sc-table th { text-align: left; font-size: 10.5px; font-weight: 600; color: #56534d; text-transform: uppercase; letter-spacing: 0.05em; padding: 7px 12px; background: #faf9f6; border-bottom: 1px solid #e4e0d9; }
.sc-table td { padding: 6px 12px; border-bottom: 1px solid #f0eee8; vertical-align: top; }
.sc-table tbody tr:nth-child(even):not(.sc-group) td { background: #f6f5f3; }
.sc-table tbody:last-child tr:last-child td { border-bottom: 0; }
.sc-hat-on td { background: #eef2f5 !important; font-weight: 600; }
.sc-family { font-size: 10px; color: #8b877f; margin-left: 8px; text-transform: uppercase; letter-spacing: 0.05em; font-weight: 400; }
.sc-group td { background: #eef2f5; border-top: 1px solid #e4e0d9; border-bottom: 1px solid #c3d2dc; padding: 5px 12px; }
.sc-group-name { font-size: 11px; font-weight: 600; color: #2a4d65; text-transform: uppercase; letter-spacing: 0.05em; margin-right: 8px; }
.sc-name { font-family: 'IBM Plex Mono', 'JetBrains Mono', monospace; font-size: 11.5px; color: #1a1918; white-space: nowrap; }
.sc-desc { color: #56534d; line-height: 1.4; }
.sc-ok { color: #2f8f5f; font-weight: 700; margin-right: 8px; }
.sc-x { color: #c23a28; font-weight: 700; margin-right: 8px; }
.sc-no .sc-name { font-family: 'IBM Plex Sans', Helvetica, Arial, sans-serif; white-space: normal; }
</style>
